import AmplitudeEventManager from './amplitudeEventManager';
import FirebaseAuthManager from './firebaseAuthManager';
import FirebaseEventManager from './firebaseEventManager';
import { setUserId } from 'firebase/analytics';
import JwtDecode from 'jwt-decode';
import ApiManager, { AuthData } from './ApiManager';

export function generateLocalStorageKey(productName: string) {
  const name = productName.split(/(?=[A-Z])/).join('_').toLowerCase();
  return `ALFRED_${name.toUpperCase()}_SETTINGS`;
}

export function createJIDSuffix(deviceName = 'Web', deviceType?: string): string {
  const JID_PREFIX = 'ivuu';
  const DEVICE_NUM = 1;

  return `${JID_PREFIX}${deviceType === 'camera' ? 1 : 3}${DEVICE_NUM}${deviceName}${new Date().getTime().toString().substr(-12, 12)}`;
}

export function removeAuth (localStorageKey: string) {
  const rawdata = localStorage.getItem(localStorageKey);
  if (rawdata) {
    const data = JSON.parse(rawdata);
    delete data.auth;
    if (Object.keys(data).length <= 0) {
      localStorage.removeItem(localStorageKey);
    } else {
      localStorage.setItem(localStorageKey, JSON.stringify(data));
    }
  }
}

export async function handleLogout (localStorageKey: string) {
  const firebaseAuthManager = new FirebaseAuthManager();
  const firebaseEventManager = new FirebaseEventManager();
  await firebaseAuthManager.signOut();
  AmplitudeEventManager.resetUser();
  firebaseEventManager.clearUserProperties();
  removeAuth(localStorageKey);
}

export default async function handleLogin({
  localStorageKey,
  jidSuffix,
  deviceName,
  deviceType,
  handleSignOut = () => handleLogout(localStorageKey),
}: {
  localStorageKey: string;
  jidSuffix?: string;
  deviceName?: string;
  deviceType?: string;
  handleSignOut?: () => void;
}) {
  try {
    let loginType = 'manual';
    let provider = '';
    let startTime = Date.now();
    let alfredIsLogin = false;
    const firebaseAuthManager = new FirebaseAuthManager();
    const firebaseEventManager = new FirebaseEventManager();

    const firebaseIsLogin = await firebaseAuthManager.isLogin();
    const user = firebaseAuthManager.getCurrentUser();
    if (firebaseIsLogin && user) {
      const idToken = await user.getIdToken();
      const decodeInfo = JwtDecode(idToken) as
        | { firebase: { sign_in_provider: string } }
        | undefined;
      const providerList: Record<string, string> = {
        password: 'email',
        'google.com': 'google',
        'apple.com': 'apple',
      };
      loginType = 'quick';
      provider =
        (decodeInfo &&
          (providerList[decodeInfo.firebase.sign_in_provider] ||
            decodeInfo.firebase.sign_in_provider)) ||
        '';
      AmplitudeEventManager.setSignInChannel(provider);
      startTime = Date.now();
      let authData: AuthData | null = null;
      let uniqueId = '';
      const localStorageData = localStorage.getItem(localStorageKey);
      if (localStorageData) {
        authData = JSON.parse(localStorageData).auth;
        if (
          authData &&
          authData.refreshToken &&
          authData.kvToken &&
          authData.expireAt &&
          authData.uid
        ) {
          if (authData.email === user.email || authData.firebaseUid === user.uid) {
            try {
              let kvToken = authData.kvToken;
              if (Date.now() > authData.expireAt) {
                const res = await ApiManager.updateToken({
                  refreshToken: authData.refreshToken,
                  jid: authData.jid,
                  firebaseUid: user.uid,
                });
                if (res) {
                  const jid = authData.jid;
                  AmplitudeEventManager.setDefaultEventProperties({ jid });
                  const newAuthData: AuthData = {
                    firebaseUid: user.uid,
                    kvToken: res.kv_token,
                    refreshToken: res.refreshToken,
                    jid,
                    email: authData.email,
                    uid: authData.uid,
                    expireAt: startTime + ApiManager.tokenTimeoutTime,
                  };
                  localStorage.setItem(
                    localStorageKey,
                    JSON.stringify({
                      auth: newAuthData,
                    })
                  );
                  kvToken = res.kv_token;
                }
              }
              ApiManager.createAxiosInstance({
                kvToken,
                uid: authData.uid,
              });
              alfredIsLogin = true;
              uniqueId = authData.uid;
            } catch (error) {
              console.log(error);
              handleSignOut();
            }
          } else {
            handleSignOut();
          }
        } else {
          handleSignOut();
        }
      } else {
        try {
          const resource = jidSuffix || createJIDSuffix(deviceName, deviceType);
          authData = await ApiManager.getKvTokenAndResetData({
            firebaseUid: user.uid,
            idToken,
            resource,
            localStorageKey,
            startTime,
          });
          AmplitudeEventManager.setDefaultEventProperties({ jid: authData.jid });
          uniqueId = authData.uid;
          alfredIsLogin = true;
        } catch (error) {
          if (error instanceof Error) {
            throw new Error(error.message);
          }
        }
      }

      AmplitudeEventManager.setUserProperties({
        uniqueId,
      });

      setUserId(firebaseEventManager.analytics, uniqueId)

      return {
        alfredIsLogin,
        loginType,
        provider,
        startTime,
        authData: authData || undefined,
        displayName: user.displayName,
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}
